import UpshowLogger from './Logger';
import fit_ui_container from './libs/fit_ui_container';
import { clearTeslaCache, detachMessageListener, spaceQuotaTeslaCache, getTeslaCache, patchAndNotifyState, fetchAndCacheState } from './Upshow';
import { getWebviewName } from './utils/activation';
import TrackerService from './services/TrackerService';
import { testSpeed } from './services/SpeedtestService';
import StateService from './services/StateService';
import { ACCESS_POINT_DISABLE_OUTCOME, disconnectAndReboot, disableAccessPoint, getEthernetLink, reboot } from '@upshow/brightsign/src';
import MetricsService from './services/MetricsService';
import { whisperToPlayers } from './services/PlayerBridgeRegistry';
import { runBootTest } from './states/readiness/bootOverlay';

const STATIC_EXTENSION_ID = process.env.REACT_APP_KIOSK_EXTENSION_ID;
const processedCommandsIds = [];

const getDeactivatedUrl = () => {

    const searchParams = (new URL(location)).searchParams;
    searchParams.set('boot_source', 'ui-restart');
    searchParams.delete('api_token');
    const restartOrigin = window.location.origin;
    const restartPath = window.location.pathname;
    let restart = restartOrigin + restartPath

    const webviewName = getWebviewName();

    if (webviewName) {

        if (webviewName.type === 'hash') {
            restart = restart.replace(`#${webviewName}`, '');
        }

        if (webviewName.type === 'params') {
            searchParams.delete('name');
        }
    }

    return `${restart}?${searchParams.toString()}`;
};

function sendToSW(message) {
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage(message);
    }
}

class CommandProcessor {
    process(command, source) {
        if (!command.hasOwnProperty('command')) {
            UpshowLogger.error(this.constructor.name, 'Command does not have correct format');
            return;
        }

        if (processedCommandsIds.includes(command.payload.uuid)) {
            return;
        }
        processedCommandsIds.push(command.payload.uuid);

        UpshowLogger.log(this.constructor.name, 'Processing ' + command.command + ' from source ' + source);
        TrackerService.appEvents.logMetric({ action: 'command', command, source });

        switch (command.command) {
            case 'identify':
                let element = document.createElement('div');
                element.setAttribute('id', 'identify_notification');
                element.innerHTML = '<i></i>';
                if (command.payload?.name) element.innerHTML = `<i/><span>${command.payload.name}</span>`;
                document.body.insertBefore(element, document.getElementById('root'));
                window.setTimeout(() => {
                    document.body.removeChild(element);
                }, 15000);
                break;
            case 'restart':
                //let's add the restart reason
                const restartUrl = `${window.location.href}${window.location.href.indexOf('?') === -1 ? '?' : '&'}boot_source=ui-restart`;
                window.location = restartUrl;
                break;
            case 'overscan':
                window.overscan = command.parameters.enabled;
                fit_ui_container();
                break;
            case 'fit_ui_container':
                fit_ui_container();
                break;
            case 'report_window_size':
                UpshowLogger.log(['report_window_size'], `width: ${window.innerWidth}, height: ${window.innerHeight}`);
                break;
            case 'clear_teslacache':
            case 'clearteslacache':
            case 'clear_tesla_cache':
                clearTeslaCache();
                break;
            case 'spacequota':
                spaceQuotaTeslaCache();
                break;
            case 'delete':
                clearTeslaCache();
                window.localStorage.clear();
                sendToSW({ type: 'CLEAR_CACHE' });

                const restart = getDeactivatedUrl();

                window.location = restart;
                break;
            case 'get_teslacache':
            case 'getteslacache':
                getTeslaCache();
                break;
            case 'cache_clear':
                //The token was deleted. We'll activate on reload @todo review
                UpshowLogger.log(['CommandProcessor', 'cache_clear'], 'Will clear localstorage ');
                const currentToken = window.localStorage.getItem('access-token');

                window.localStorage.clear();
                UpshowLogger.log(['CommandProcessor', 'cache_clear'], 'Cleared localstorage ');

                if (currentToken) {
                    window.localStorage.setItem('access-token', currentToken);
                }
                break;
            case 'clear_sw_cache':
                sendToSW({ type: 'CLEAR_CACHE' });
                break;
            case 'get_sw_quota':
                sendToSW({ type: 'QUOTA_ESTIMATE' });
                break;
            case 'get_sw_keys':
                sendToSW({ type: 'GET_KEYS', name: command.payload?.name });
                break;
            case 'delete_sw_key':
                if (!command.payload?.key) UpshowLogger.error('CommandProcessor', 'delete_sw_key missing key');
                sendToSW({ type: 'DELETE_KEY', name: command.payload?.name, key: command.payload.key });
                break;
            case 'deponger':
                detachMessageListener();
                break;
            case 'advance_state':
                StateService.advanceState();
                break;
            case 'speed_test':
                testSpeed(command.payload?.uuid, command.payload?.resource, command.payload?.resourceLength, command.payload?.idTag);
                break;
            case 'gc_cleanup':
                StateService.gc(true);
                break;
            case 'clear_browser_cache':
                if (chrome.runtime) {
                    chrome.runtime.sendMessage(STATIC_EXTENSION_ID, {
                        methodName: 'removeCache',
                    }, function (arg) {
                        if (chrome.runtime.lastError) {
                            console.error({ error: chrome.runtime.lastError });
                        } else {
                            console.log(arg);
                        }
                    });
                }
                break;
            case 'update_live_content':
                if (command.payload) {
                    const { activeLiveContent, activeLiveEvent, upcomingLiveContent, timestamp } = command.payload;
                    // Use activeLiveContent if available, fallback to activeLiveEvent for backward compatibility
                    const liveContent = activeLiveContent || activeLiveEvent;
                    StateService.setLiveContentStatus(liveContent, timestamp);
                    const liveContentPatch = { activeLiveContent: liveContent };
                    if ('upcomingLiveContent' in command.payload) {
                        liveContentPatch.upcomingLiveContent = upcomingLiveContent ?? [];
                    }
                    patchAndNotifyState(liveContentPatch);
                    StateService.syncScheduledContentState();
                    // When a test stream ends via socket push, re-sync /state so
                    // isSubscribedToTestStream flips in lockstep with the content change.
                    if (window.__UpshowState?.isSubscribedToTestStream && !liveContent) {
                        fetchAndCacheState();
                    }
                } else {
                    UpshowLogger.error('CommandProcessor', 'Skipping update_live_content command: missing live content');
                }
                break;
            case 'player_override':
                if (command.payload && typeof command.payload === 'object') {
                    const sent = whisperToPlayers('applyOverride', command.payload);
                    if (sent === 0) {
                        UpshowLogger.warn('CommandProcessor', 'player_override: no player currently on screen');
                    }
                } else {
                    UpshowLogger.error('CommandProcessor', 'Skipping player_override command: missing payload');
                }
                break;

            case 'run_checks':
                const div = document.createElement('div');
                const iframe = document.createElement('iframe');
                const checksContainerRef = document.getElementById('checksContainer');

                if (!checksContainerRef) {
                    UpshowLogger.log(['CommandProcessor', 'run_checks'], 'Running UI Checks');
                    iframe.setAttribute('src', 'https://checks.upshow.tv/');
                    iframe.setAttribute('id', 'checksIframe');
                    div.setAttribute('id', 'checksContainer');
                    div.appendChild(iframe);
                    document.body.appendChild(div);

                    const messageHandler = (event) => {
                            if (event.data?.type === 'check_result') {
                                UpshowLogger.log(['CommandProcessor', 'check_result'], `${event.data.label}: ${event.data.result}`);
                            }
                        };
                        window.addEventListener('message', messageHandler);

                        setTimeout(() => {
                            const checksContainerRef = document.getElementById('checksContainer');         
                            checksContainerRef.remove();
                            window.removeEventListener('message', messageHandler);
                        }, 45000)
                }
                break;
            case 'run_boot_test':
                UpshowLogger.log(['CommandProcessor', 'run_boot_test'], 'Running readiness boot test');
                runBootTest().catch(err => UpshowLogger.error(['CommandProcessor', 'run_boot_test'], err));
                break;
            case 'reset_brightsign_wifi':
                UpshowLogger.log(['CommandProcessor', 'reset_brightsign_wifi'], 'Reset Wi-Fi');
                disconnectAndReboot();
                break;
            case 'disable_brightsign_access_point':
                UpshowLogger.log(['CommandProcessor', 'disable_brightsign_access_point'], 'Checking BrightSign Ethernet link before disabling access point');
                getEthernetLink().then(async ethernetStatus => {
                    if (!ethernetStatus.available) {
                        UpshowLogger.error(['CommandProcessor', 'disable_brightsign_access_point'], `Unable to verify Ethernet link: ${ethernetStatus.reason}`);
                        return;
                    }

                    if (!ethernetStatus.hasLink) {
                        UpshowLogger.log(['CommandProcessor', 'disable_brightsign_access_point'], 'Skipping access-point disablement: no Ethernet link');
                        return;
                    }

                    UpshowLogger.log(['CommandProcessor', 'disable_brightsign_access_point'], 'Ethernet link confirmed; disabling BrightSign access point');
                    const result = await disableAccessPoint();
                    switch (result.outcome) {
                        case ACCESS_POINT_DISABLE_OUTCOME.UNAVAILABLE:
                        case ACCESS_POINT_DISABLE_OUTCOME.FAILED:
                            UpshowLogger.error(['CommandProcessor', 'disable_brightsign_access_point'], result.reason);
                            return;
                        case ACCESS_POINT_DISABLE_OUTCOME.ALREADY_DISABLED:
                            UpshowLogger.log(['CommandProcessor', 'disable_brightsign_access_point'], 'BrightSign access point already disabled');
                            return;
                        case ACCESS_POINT_DISABLE_OUTCOME.DISABLED: {
                            UpshowLogger.log(['CommandProcessor', 'disable_brightsign_access_point'], 'BrightSign access point disabled; rebooting');
                            const rebooted = await reboot();
                            if (!rebooted) UpshowLogger.error(['CommandProcessor', 'disable_brightsign_access_point'], 'Failed to send BrightSign reboot command');
                            break;
                        }
                        default:
                            UpshowLogger.error(['CommandProcessor', 'disable_brightsign_access_point'], `Unexpected access-point outcome: ${result.outcome}`);
                            return;
                    }
                }).catch(error => UpshowLogger.error(['CommandProcessor', 'disable_brightsign_access_point'], error));
                break;
            case 'socket-metrics':
                if (command.payload?.enabled !== undefined) {
                    if (command.payload.enabled) {
                        MetricsService.enable();
                        UpshowLogger.log(['CommandProcessor', 'socket-metrics'], 'Metrics enabled');
                    } else {
                        MetricsService.disable();
                        UpshowLogger.log(['CommandProcessor', 'socket-metrics'], 'Metrics disabled');
                    }
                }
                break;
            default:
                UpshowLogger.info('CommandProcessor', 'Skipping unknown command: ' + command.command);
                break;
        }
    }
}

export default CommandProcessor;
